/**
 * Fix: on a fresh page load with an EMPTY cart, adding the first item can
 * leave the cart drawer showing "Your cart is empty" even though the item
 * was actually added server-side (confirmed via /cart.js). This appears to
 * be a theme core bug in how the section-rendering payload is requested on
 * the very first add-to-cart of a session, so the drawer's stale "empty"
 * markup from initial page load never gets replaced.
 *
 * This is a small, additive safety net that does not modify any theme core
 * file. It triggers two ways (belt and braces, since relying on only one
 * theme event proved unreliable in testing):
 *   1. Whenever the theme's own "cart:update" event fires.
 *   2. Whenever the cart drawer's <dialog> element is actually opened
 *      (native `open` attribute), which is the moment a shopper would
 *      actually see whatever is wrong.
 *
 * Either way, it only acts if the drawer is showing the empty-cart state
 * while the real cart (per /cart.js) is not empty - otherwise it no-ops
 * immediately.
 */
(function () {
  let checking = false;

  async function checkAndFix() {
    if (checking) return;
    checking = true;
    try {
      const drawerItems = document.querySelector('cart-items-component[data-drawer]');
      if (!drawerItems) return;

      const showsEmptyState = !!drawerItems.querySelector('.cart-items__empty-button')
        && !drawerItems.querySelector('.cart-form');
      if (!showsEmptyState) return;

      const cart = await fetch('/cart.js').then((r) => r.json());
      if (!cart || !cart.item_count) return; // genuinely empty, nothing to fix

      const sectionId = drawerItems.dataset.sectionId;
      if (!sectionId) return;

      const url = new URL(window.location.href);
      url.searchParams.set('section_id', sectionId);

      const html = await fetch(url.toString()).then((r) => r.text());
      const fragment = new DOMParser().parseFromString(html, 'text/html');

      const newWrapper = fragment.querySelector('cart-items-component[data-drawer] .cart-items__wrapper');
      const currentWrapper = drawerItems.querySelector('.cart-items__wrapper');
      if (newWrapper && currentWrapper) {
        currentWrapper.replaceWith(newWrapper);
      }

      const newHeading = fragment.querySelector('.cart-drawer__heading');
      const currentHeading = document.querySelector('.cart-drawer__heading');
      if (newHeading && currentHeading) {
        currentHeading.replaceWith(newHeading);
      }

      const currentDialog = document.querySelector('.cart-drawer__dialog');
      const newDialog = fragment.querySelector('.cart-drawer__dialog');
      if (currentDialog && newDialog) {
        currentDialog.className = newDialog.className;
      }
    } catch (error) {
      console.error('[cart-drawer-empty-fix]', error);
    } finally {
      checking = false;
    }
  }

  function schedule() {
    setTimeout(checkAndFix, 350);
  }

  document.addEventListener('cart:update', schedule);

  function observeDrawer() {
    const dialog = document.querySelector('cart-drawer-component dialog');
    if (!dialog) {
      setTimeout(observeDrawer, 300);
      return;
    }
    const observer = new MutationObserver(function (mutations) {
      for (const mutation of mutations) {
        if (mutation.attributeName === 'open' && dialog.hasAttribute('open')) {
          schedule();
        }
      }
    });
    observer.observe(dialog, { attributes: true, attributeFilter: ['open'] });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observeDrawer);
  } else {
    observeDrawer();
  }
})();
